import type { Post } from "./types";
import heroAvslag from "@/assets/news/lana-pengar-utan-avslag.jpg";

export const POSTS_18: Record<string, Post> = {
  "lana-pengar-utan-avslag": {
    title: "Låna pengar utan avslag – så ökar du chansen 2026",
    intro:
      "Har du fått nej flera gånger och känner att varje ny ansökan bara gör saken värre? Vi förstår – ett avslag känns personligt, men det är nästan alltid ett automatiskt beslut i ett system. Här går vi igenom varför avslagen kommer, vilka långivare som har högst beviljandegrad 2026, vad det kostar i kronor och vilka konkreta steg som faktiskt vänder ett nej till ett ja.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "6 september 2026",
    updated: "6 september 2026",
    heroSrc: heroAvslag,
    heroAlt: "Person sitter med mobil och räkningar och funderar på att låna pengar utan avslag",
    topLenderId: "credifi",
    bottomLenderId: "lumify",
    topLenderTitle: "Populärast bland dig som fått avslag tidigare",
    bottomLenderTitle: "Vår rekommendation vid tidigare avslag",
    reviewsLenderId: "lumify",
    cta: { to: "/lan-utan-uc", label: "Jämför långivare med hög beviljandegrad" },
    calculator: {
      title: "Räkna på att låna pengar utan avslag",
      intro:
        "Innan du ansöker igen: se vad lånet kostar. Dra i reglagen för månadskostnad, räntekostnad, total lånekostnad och effektiv ränta.",
      note:
        "Månadskostnaden styrs av lånebelopp, löptid och nominell ränta, men det är den effektiva räntan som visar din verkliga kostnad eftersom uppläggningsavgift och aviavgift räknas in. En lång löptid ger lägre månadskostnad men klart högre total lånekostnad. Testa flera löptider innan du signerar.",
      amount: 10000,
      minAmount: 1000,
      maxAmount: 50000,
      months: 12,
      maxMonths: 60,
      rate: 23.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 350,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Finns det lån helt utan avslag?",
        a: "Nej. Alla långivare med tillstånd från Finansinspektionen måste enligt konsumentkreditlagen göra en kreditprövning, och den kan sluta med ett nej. Däremot skiljer sig beviljandegraden kraftigt mellan aktörerna, och det går att påverka chansen.",
      },
      {
        q: "Varför får jag avslag trots fast inkomst?",
        a: "Vanligast är många kreditförfrågningar på kort tid, hög total skuldkvot eller att inkomsten inte hunnit registreras. Systemet väger ihop flera signaler, så en enskild bra faktor räcker inte alltid.",
      },
      {
        q: "Hur länge ska jag vänta efter ett avslag?",
        a: "Vänta gärna 30 dagar innan du ansöker på nytt hos samma långivare. Nya förfrågningar tätt inpå sänker din kreditvärdighet ytterligare och ökar risken för fler avslag.",
      },
      {
        q: "Hjälper det att söka utan UC?",
        a: "Ofta ja, om du redan har många UC-förfrågningar. Långivare som använder Creditsafe eller Dun & Bradstreet ser en annan bild, men kreditprövningen görs fortfarande och kan sluta med nej.",
      },
      {
        q: "Ökar en medsökande chansen?",
        a: "Ja, tydligt. Två inkomster sänker den bedömda risken, och många långivare beviljar både högre belopp och lägre ränta när ansökan görs tillsammans med någon som har ordnad ekonomi.",
      },
      {
        q: "Vad kostar det att låna 10 000 kr när man tidigare fått avslag?",
        a: "Räntan hamnar ofta kring 24 procent. Med 350 kr i uppläggningsavgift och 29 kr per avi blir månadskostnaden cirka 975 kr på tolv månader och totalkostnaden runt 11 700 kr.",
      },
      {
        q: "Kan jag ångra lånet om jag ändrar mig?",
        a: "Ja, du har 14 dagars ångerrätt enligt lag. Du betalar tillbaka beloppet plus ränta för de dagar du haft pengarna och slipper resten av kostnaden.",
      },
    ],
    blocks: [
      { t: "h2", text: "Låna pengar utan avslag – kort svar" },
      { t: "p", text: "Att låna pengar utan avslag i bokstavlig mening finns inte. Varje seriös långivare måste göra en kreditprövning. Men beviljandegraden skiljer sig kraftigt mellan aktörerna, och det finns konkreta saker du kan göra som märkbart ökar chansen att få ja." },
      { t: "p", text: "Vi tänker inte lova dig något garanterat godkännande. Vi tänker däremot visa var chansen faktiskt är störst, vad det kostar, och när du borde välja något annat än ett nytt lån." },
      {
        t: "stats",
        items: [
          { label: "Vanligaste avslagsorsaken", value: "Många förfrågningar" },
          { label: "Typisk ränta efter avslag", value: "19–29 %" },
          { label: "Rekommenderad väntetid", value: "30 dagar" },
        ],
      },

      { t: "h2", text: "Varför avslagen kommer – de sex vanligaste orsakerna" },
      { t: "p", text: "Nästan alla avslag går att spåra till en handfull faktorer. Känner du igen din situation blir det också tydligt vad som går att åtgärda." },
      {
        t: "table",
        caption: "Vanliga avslagsorsaker och vad du kan göra",
        head: ["Orsak", "Påverkan", "Går att förbättra"],
        rows: [
          ["Många kreditförfrågningar", "Stor", "Ja – vänta 30 dagar"],
          ["Betalningsanmärkning", "Mycket stor", "Nej – tiden avgör"],
          ["Skuld hos Kronofogden", "Mycket stor", "Ja – betala av"],
          ["Låg eller oregelbunden inkomst", "Stor", "Ja – på sikt"],
          ["Hög skuldkvot", "Stor", "Ja – lös eller samla lån"],
          ["Kort tid på adressen", "Liten", "Ja – med tiden"],
        ],
      },
      { t: "p", text: "Har du redan flera förfrågningar registrerade kan det vara läge att läsa vår guide om [lån utan UC med många förfrågningar](/nyhet/lan-utan-uc-manga-forfragningar)." },

      { t: "h2", text: "Vilka långivare har högst beviljandegrad?" },
      { t: "p", text: "Beviljandegraden hänger ihop med hur långivaren bedömer risk och vilket kreditupplysningsbolag som används." },
      { t: "h3", text: "Nischlångivare utan UC" },
      { t: "p", text: "Använder Creditsafe eller Dun & Bradstreet och accepterar ofta lägre kreditvärdighet. Räntan ligger normalt mellan 19 och 29 procent." },
      { t: "h3", text: "Låneförmedlare" },
      { t: "p", text: "En förfrågan skickas till många banker samtidigt. Chansen att någon säger ja ökar utan att du samlar på dig förfrågningar." },
      { t: "h3", text: "Storbanker" },
      { t: "p", text: "Lägst ränta men hårdast krav. Har du fått avslag på nischlån är chansen liten här." },

      { t: "h2", text: "Sju steg som ökar chansen att bli beviljad" },
      {
        t: "steps",
        title: "Gör så här innan nästa ansökan",
        items: [
          "Vänta minst 30 dagar sedan senaste avslaget.",
          "Beställ din egen kreditupplysning och kontrollera uppgifterna.",
          "Lös eller samla små dyra krediter först.",
          "Sök lägsta möjliga belopp – inte det du blir erbjuden.",
          "Lägg till en medsökande om det är möjligt.",
          "Ange all inkomst, även bidrag, pension och a-kassa.",
          "Använd en låneförmedlare i stället för många enskilda ansökningar.",
        ],
      },

      { t: "h2", text: "Vad ett lån kostar när du tidigare fått avslag" },
      { t: "p", text: "Räntan sätts efter risk. Har du fått avslag tidigare hamnar du oftast i det övre spannet." },
      {
        t: "table",
        caption: "Räkneexempel: 10 000 kr, annuitetslån, 23,9 % nominell ränta",
        head: ["Löptid", "Månadskostnad", "Räntekostnad", "Total kostnad", "Effektiv ränta"],
        rows: [
          ["6 mån", "1 812 kr", "604 kr", "10 954 kr", "43,6 %"],
          ["12 mån", "975 kr", "1 332 kr", "11 682 kr", "33,7 %"],
          ["24 mån", "558 kr", "2 673 kr", "13 319 kr", "28,6 %"],
          ["36 mån", "424 kr", "4 083 kr", "14 477 kr", "26,8 %"],
        ],
      },
      { t: "p", text: "Räkna på dina egna siffror i vår [lånekalkyl](/guider/lanekalkyl) innan du signerar." },

      { t: "h2", text: "Effektiv ränta – måttet som gör erbjudanden jämförbara" },
      { t: "p", text: "Nominell ränta räcker inte eftersom avgifterna ligger utanför. Effektiv ränta väger in uppläggningsavgift, aviavgift och löptid." },
      {
        t: "box",
        tone: "tip",
        title: "Tumregler",
        items: [
          "Jämför alltid effektiv ränta, aldrig månadskostnad.",
          "Låg månadskostnad betyder ofta lång löptid och högre totalkostnad.",
          "Be om totalbeloppet i kronor – det ska stå i avtalet.",
          "Uppläggningsavgiften slår hårdast på små och korta lån.",
        ],
      },

      { t: "h2", text: "Lån utan UC när avslagen redan kommit" },
      { t: "p", text: "Har du många UC-förfrågningar kan en långivare som använder Creditsafe se en annan bild av din ekonomi. Kreditprövningen görs fortfarande, men förfrågan syns inte i UC-historiken. Se alternativen på vår sida om [lån utan UC](/lan-utan-uc)." },

      { t: "h2", text: "Med betalningsanmärkning eller skuld hos Kronofogden" },
      { t: "p", text: "Med en aktiv skuld hos Kronofogden är det svårare att bli godkänd. Varje ansökan bedöms individuellt av långivaren. Är skulden reglerad ökar chansen märkbart." },
      { t: "p", text: "Läs mer om vad som gäller i vår artikel om [lån trots Kronofogden](/nyhet/sms-lan-trots-kronofogden)." },

      { t: "h2", text: "Krav som nästan alla långivare ställer" },
      {
        t: "ul",
        items: [
          "Minst 18–20 år beroende på långivare.",
          "Folkbokförd i Sverige och svenskt BankID.",
          "Regelbunden och verifierbar inkomst.",
          "Eget bankkonto i ditt namn.",
          "Ofta inga aktiva mål hos Kronofogden.",
        ],
      },

      { t: "h2", text: "För- och nackdelar med att söka igen direkt" },
      {
        t: "proscons",
        pros: [
          "Du kan få pengarna samma dag",
          "Digital process med BankID",
          "Ångerrätt i 14 dagar",
          "Rätt att lösa lånet i förtid",
        ],
        cons: [
          "Varje ny förfrågan sänker kreditvärdigheten",
          "Hög effektiv ränta efter tidigare avslag",
          "Risk för skuldspiral vid upprepade lån",
          "Uppläggningsavgift på små belopp",
        ],
      },

      { t: "h2", text: "Billigare alternativ innan du lånar" },
      {
        t: "ul",
        items: [
          "Anstånd hos fordringsägaren – ofta gratis.",
          "Avbetalningsplan direkt hos företaget.",
          "Outnyttjad kontokredit du redan har.",
          "Förskott från arbetsgivaren.",
          "Kommunens budget- och skuldrådgivning – kostnadsfri och opartisk.",
        ],
      },
      { t: "p", text: "Har du flera dyra krediter kan ett [samlingslån](/samla-lan) sänka både ränta och månadskostnad." },

      { t: "h2", text: "Ditt skydd i lagen" },
      { t: "p", text: "Konsumentkreditlagen sätter tak. Den nominella räntan får inte överstiga Riksbankens referensränta plus 20 procentenheter, och de samlade kostnaderna får aldrig överstiga lånebeloppet." },
      {
        t: "box",
        tone: "info",
        title: "Det här gäller alltid",
        items: [
          "14 dagars ångerrätt utan angiven anledning.",
          "Rätt att lösa lånet i förtid utan extra avgift.",
          "Obligatorisk kreditprövning hos alla seriösa aktörer.",
          "Långivaren måste ha tillstånd från Finansinspektionen.",
        ],
      },

      { t: "h2", text: "Så känner du igen ett oseriöst erbjudande" },
      {
        t: "box",
        tone: "warning",
        title: "Varningssignaler",
        items: [
          "Löfte om lån helt utan kreditprövning eller garanterat godkännande.",
          "Avgift som ska betalas i förskott.",
          "Kontakt enbart via sms, WhatsApp eller Messenger.",
          "Ingen adress, inget organisationsnummer och inga fullständiga villkor.",
        ],
      },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, utbetalningstid, tydlighet i villkoren, tillstånd hos Finansinspektionen och kundomdömen. Ingen långivare kan köpa sig till en placering. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Lån helt utan avslag finns inte, men chansen går att påverka. Vänta 30 dagar, sök lägsta möjliga belopp, överväg medsökande och använd en förmedlare eller en långivare som prövar utan UC. Och räkna alltid på totalkostnaden innan du signerar." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och ångerrätt",
          "[Finansinspektionen](https://www.fi.se) – tillståndsregister och tillsyn",
          "[Kronofogden](https://www.kronofogden.se) – skulder och betalningsanmärkningar",
        ],
      },
    ],
  },
};
